import { ValueSet } from '../../types';
import {
  PREFERENCE_VALIDATION_RULES,
  PreferenceRule,
  getAllowedValues,
  isValidEnum,
  getDefaultValue,
} from './preference.validation.config';
import { PreferenceValidationErrors, ValidationErrorDetail } from './preference.validation.errors';

type ValidatorFunction = (
  fieldPath: string,
  value: any,
  rule: PreferenceRule
) => { errors: ValidationErrorDetail[]; shouldStop: boolean };

export class PreferenceValidator {
  private static _instance: PreferenceValidator;
  private readonly validators: ValidatorFunction[];

  private constructor() {
    this.validators = [
      this.validateNull,
      this.validateEmptyString,
      this.validateBoolean,
      this.validateCustom,
      this.validateEnum,
      this.validateStringLength,
    ];
  }

  static getInstance(): PreferenceValidator {
    if (!PreferenceValidator._instance) {
      PreferenceValidator._instance = new PreferenceValidator();
    }
    return PreferenceValidator._instance;
  }

  /**
   * Validates and sanitizes preference data.
   * Only substitutes defaults for empty/null/undefined values when not allowed.
   * Other validation errors will still throw.
   */
  validate(name: string, preference: ValueSet): ValueSet {
    const sanitized: ValueSet = {};
    const errors: ValidationErrorDetail[] = [];

    for (const [componentKey, componentSettings] of Object.entries(preference)) {
      sanitized[componentKey] = {};

      for (const [fieldKey, fieldValue] of Object.entries(componentSettings)) {
        const { value, fieldErrors, wasSubstituted } = this.validateAndSanitizeField(fieldKey, fieldValue);

        if (fieldErrors.length > 0 && !wasSubstituted) {
          errors.push(...fieldErrors);
        }

        sanitized[componentKey][fieldKey] = value;
      }
    }

    if (errors.length > 0) {
      throw new PreferenceValidationErrors(
        `Validation failed for preference "${name}" with ${errors.length} error(s)`,
        errors
      );
    }

    return sanitized;
  }

  private validateAndSanitizeField(
    field: string,
    value: any
  ): { value: any; fieldErrors: ValidationErrorDetail[]; wasSubstituted: boolean } {
    const errors: ValidationErrorDetail[] = [];
    const rule = PREFERENCE_VALIDATION_RULES[field];

    if (!rule) {
      return { value, fieldErrors: errors, wasSubstituted: false };
    }

    // Handle undefined values
    if (value === undefined) {
      const defaultValue = !rule.allowUndefined ? getDefaultValue(field) : undefined;
      const hasDefault = defaultValue !== undefined;
      return {
        value: hasDefault ? defaultValue : value,
        fieldErrors: [],
        wasSubstituted: hasDefault,
      };
    }

    // Validate array structure
    const isValueArray = Array.isArray(value);
    const expectsArray = rule.isArray !== false;

    if (isValueArray !== expectsArray) {
      const structureError: ValidationErrorDetail[] = [
        {
          field,
          value,
          message: expectsArray
            ? `Expected an array but got ${typeof value}`
            : `Expected a single value but got an array`,
          rule: 'isArray',
        },
      ];
      return { value, fieldErrors: structureError, wasSubstituted: false };
    }

    // Process based on value type
    if (Array.isArray(value)) {
      return this.processArrayValue(field, value, rule);
    }

    // Process single value
    const { value: sanitizedValue, errors: valueErrors, shouldSubstitute } = this.validateValue(field, value, rule);
    return {
      value: sanitizedValue,
      fieldErrors: valueErrors,
      wasSubstituted: shouldSubstitute,
    };
  }

  private processArrayValue(
    field: string,
    value: any[],
    rule: PreferenceRule
  ): { value: any; fieldErrors: ValidationErrorDetail[]; wasSubstituted: boolean } {
    // Handle empty arrays
    if (!rule.allowEmpty && value.length === 0) {
      return { value: getDefaultValue(field) ?? [], fieldErrors: [], wasSubstituted: true };
    }

    const sanitizedArray: any[] = [];
    const arrayErrors: ValidationErrorDetail[] = [];
    let hasSubstitutions = false;
    let hasNonSubstitutableErrors = false;

    for (let i = 0; i < value.length; i++) {
      const {
        value: sanitizedValue,
        errors: elementErrors,
        shouldSubstitute,
      } = this.validateValue(field, value[i], rule, `[${i}]`);

      if (elementErrors.length === 0) {
        sanitizedArray.push(sanitizedValue);
        continue;
      }

      if (shouldSubstitute) {
        sanitizedArray.push(sanitizedValue !== undefined ? sanitizedValue : value[i]);
        hasSubstitutions = true;
        continue;
      }

      sanitizedArray.push(value[i]);
      arrayErrors.push(...elementErrors);
      hasNonSubstitutableErrors = true;
    }

    return {
      value: sanitizedArray,
      fieldErrors: arrayErrors,
      wasSubstituted: hasSubstitutions && !hasNonSubstitutableErrors,
    };
  }

  private validateValue(
    field: string,
    value: any,
    rule: PreferenceRule,
    suffix = ''
  ): { value: any; errors: ValidationErrorDetail[]; shouldSubstitute: boolean } {
    const fieldPath = `${field}${suffix}`;

    for (const validator of this.validators) {
      const result = validator.call(this, fieldPath, value, rule);

      if (!result.shouldStop) {
        continue;
      }

      if (result.errors.length === 0) {
        return { value, errors: [], shouldSubstitute: false };
      }

      const isEmptyRelatedError = result.errors.some(e =>
        ['allowEmpty', 'allowNull', 'allowUndefined'].includes(e.rule)
      );

      if (!isEmptyRelatedError) {
        return { value, errors: result.errors, shouldSubstitute: false };
      }

      const defaultValue = getDefaultValue(field);

      if (defaultValue === undefined) {
        return { value, errors: result.errors, shouldSubstitute: false };
      }

      // Substitute with default
      const sanitizedValue = Array.isArray(defaultValue) && defaultValue.length > 0 ? defaultValue[0] : defaultValue;

      return { value: sanitizedValue, errors: [], shouldSubstitute: true };
    }

    return { value, errors: [], shouldSubstitute: false };
  }

  private validateNull(fieldPath: string, value: any, rule: PreferenceRule) {
    if (value !== null) {
      return { errors: [], shouldStop: false };
    }

    const errors: ValidationErrorDetail[] = [];
    if (!rule.allowNull) {
      errors.push({
        field: fieldPath,
        value,
        message: 'Null value not allowed',
        rule: 'allowNull',
      });
    }
    return { errors, shouldStop: true };
  }

  private validateEmptyString(fieldPath: string, value: any, rule: PreferenceRule) {
    if (typeof value !== 'string' || value.trim() !== '') {
      return { errors: [], shouldStop: false };
    }

    const errors: ValidationErrorDetail[] = [];
    if (!rule.allowEmpty) {
      errors.push({
        field: fieldPath,
        value,
        message: 'Empty string not allowed',
        rule: 'allowEmpty',
      });
    }
    return { errors, shouldStop: true };
  }

  private validateBoolean(fieldPath: string, value: any, rule: PreferenceRule) {
    if (!rule.isBoolean) {
      return { errors: [], shouldStop: false };
    }

    const errors: ValidationErrorDetail[] = [];
    if (value !== 'true' && value !== 'false') {
      errors.push({
        field: fieldPath,
        value,
        message: `Value must be 'true' or 'false', got "${value}"`,
        rule: 'isBoolean',
      });
    }
    return { errors, shouldStop: true };
  }

  private validateCustom(fieldPath: string, value: any, rule: PreferenceRule) {
    if (!rule.customValidator) {
      return { errors: [], shouldStop: false };
    }

    const errors: ValidationErrorDetail[] = [];
    if (!rule.customValidator(value)) {
      errors.push({
        field: fieldPath,
        value,
        message: 'Failed custom validation',
        rule: 'customValidator',
      });
    }
    return { errors, shouldStop: true };
  }

  private validateEnum(fieldPath: string, value: any, rule: PreferenceRule) {
    const errors: ValidationErrorDetail[] = [];

    if (rule.enumType && !isValidEnum(rule.enumType, value)) {
      const allowedValues = getAllowedValues(rule);
      errors.push({
        field: fieldPath,
        value,
        message: `Value "${value}" not in allowed values: [${allowedValues?.join(', ')}]`,
        rule: 'enumType',
      });
      return { errors, shouldStop: true };
    } else if (rule.allowedValues && !rule.allowedValues.includes(value as string)) {
      errors.push({
        field: fieldPath,
        value,
        message: `Value "${value}" not in allowed values: [${rule.allowedValues.join(', ')}]`,
        rule: 'allowedValues',
      });
      return { errors, shouldStop: true };
    }

    return { errors, shouldStop: false };
  }

  private validateStringLength(fieldPath: string, value: any, rule: PreferenceRule) {
    const errors: ValidationErrorDetail[] = [];

    if (typeof value !== 'string') {
      return { errors, shouldStop: false };
    }

    const trimmedValue = value.trim();

    if (rule.minLength !== undefined && trimmedValue.length < rule.minLength) {
      errors.push({
        field: fieldPath,
        value,
        message: `String length ${trimmedValue.length} is less than minimum ${rule.minLength}`,
        rule: 'minLength',
      });
    }

    if (rule.maxLength !== undefined && trimmedValue.length > rule.maxLength) {
      errors.push({
        field: fieldPath,
        value,
        message: `String length ${trimmedValue.length} exceeds maximum ${rule.maxLength}`,
        rule: 'maxLength',
      });
    }

    return { errors, shouldStop: errors.length > 0 };
  }
}
